import { useCallback } from 'react';

/**
 * Hook for node creation and manipulation actions
 * 
 * Handles:
 * - Creating connected nodes (from connection handle click)
 * - Inserting nodes on edges (from edge insert button)
 * - Setting task positions
 * - Expanding focus mode when adding tasks in focus mode
 * - Triggering auto-tidy after edge insertion
 */
export function useNodeActions({
  interaction,
  findEmptySpaceNearNode,
  // findEmptySpaceNearPoint - no longer used, edge insertion calculates position directly
  onCreateConnectedTask,
  onInsertNodeOnEdge,
  onInsertMilestoneOnEdge,
  onInsertComplete, // Callback to trigger auto-tidy after insertion (receives insertData for undo)
  onCreateComplete, // Callback to trigger auto-tidy after creating a connected node
  // Focus mode integration
  isFocusMode,
  focusedTaskIds,
  allFocusedTaskIds,
  addToFocusedTasks,
}) {
  const setTaskPosition = useCallback((taskId, position) => {
    interaction.setCustomPositions(prev => ({ ...prev, [taskId]: position }));
  }, [interaction]);

  const handleCreateConnectedNode = useCallback((sourceTaskId, nodeType, branch, options) => {
    const position = findEmptySpaceNearNode(sourceTaskId, { atHandle: true });
    const newTaskId = `task-${Date.now()}`;
    setTaskPosition(newTaskId, position);
    
    // If in focus mode, expand the focused set to include the new task
    // Also promote the parent if it was only visible as upstream/downstream (faded)
    if (isFocusMode && addToFocusedTasks) {
      // Check if parent is faded (in allFocusedTaskIds but not in focusedTaskIds)
      const parentIsFaded = allFocusedTaskIds?.has(sourceTaskId) && 
                            !focusedTaskIds?.has(sourceTaskId);
      
      // Add new task to focus, and promote parent if it was faded
      addToFocusedTasks(newTaskId, parentIsFaded ? sourceTaskId : null);
    }
    
    onCreateConnectedTask?.(newTaskId, sourceTaskId, nodeType, position, branch, options);
    
    // Select the new task so its title input gets auto-focused
    interaction.setSelectedNodes(new Set([newTaskId]));

    if (onCreateComplete) {
      setTimeout(() => {
        onCreateComplete(newTaskId, sourceTaskId);
      }, 50);
    }
  }, [findEmptySpaceNearNode, setTaskPosition, onCreateConnectedTask, onCreateComplete,
      isFocusMode, focusedTaskIds, allFocusedTaskIds, addToFocusedTasks, interaction]);

  // Insert a node on an edge (between two connected nodes)
  // Position the new node centered horizontally between the from/to nodes,
  // at the vertical midpoint of the edge
  const handleInsertNodeOnEdge = useCallback((edge, nodeType, midpoint) => {
    if (nodeType === 'milestone') {
      onInsertMilestoneOnEdge?.(edge, midpoint);
      return;
    }

    // Calculate position: horizontally centered on the edge, vertically at midpoint
    // The midpoint already has good Y positioning (between parent and child)
    // For X, we center the new node on the midpoint X
    const position = {
      x: midpoint.x - 145, // Center a ~290px wide node on the midpoint
      y: midpoint.y - 75,  // Center a ~150px tall node on the midpoint
    };
    
    const newTaskId = `task-${Date.now()}`;
    setTaskPosition(newTaskId, position);
    
    // Call the handler with skipRecordAction: true
    // The undo action will be recorded AFTER the tidy completes (in handleInsertComplete)
    // so that position restoration can be included
    const insertData = onInsertNodeOnEdge?.(newTaskId, edge.from, edge.to, edge.branch, nodeType, position, { skipRecordAction: true });
    
    // Select the new task so its title input gets auto-focused
    interaction.setSelectedNodes(new Set([newTaskId]));
    
    // Trigger auto-tidy on the affected chain after a delay
    // to let the React state updates propagate (new task added, dependencies updated)
    // Pass insertData so the combined undo action can be recorded after tidy
    if (onInsertComplete) {
      setTimeout(() => {
        onInsertComplete(newTaskId, edge.from, edge.to, insertData);
      }, 150); // Longer delay to ensure state has updated
    }
  }, [setTaskPosition, onInsertNodeOnEdge, onInsertMilestoneOnEdge, interaction, onInsertComplete]);

  return {
    setTaskPosition,
    handleCreateConnectedNode,
    handleInsertNodeOnEdge,
  };
}

export default useNodeActions;

